/**
 * Silent, resumable enrolment (spec `docs/plans/2026-09-02-next-steps-e2e-encryption.md`,
 * section 6.7 "Enrolment is silent and resumable"; decision 40; API contract
 * section 4).
 *
 * The first time an enrolment-capable member opens the Notes Hub or makes a
 * highlight: generate MK, the device key pair and the bundle signing key,
 * record them as ONE durable attempt, prove the platform can carry the key,
 * then register the bundle with the API. No screen, no prompt, no toast —
 * only the one-line banner in the Hub afterwards.
 *
 * ORDER MATTERS, and it is a protocol, not error handling:
 *
 *   generate → attempt row (prepared) → provisional platform copy, read back
 *            → row goes ambiguous, durably → POST → promote (one transaction)
 *            → publish → committed platform copy, read back → clear provisional
 *
 * THE INVARIANT this order keeps: MK for a `keyId` the API has committed is
 * durable on this device BEFORE the commit and stays durable until another
 * holder is proven; and MK for a `keyId` the API has not confirmed is
 * invisible to every read and write path, on every device the platform
 * carries a copy to. The first half is why the row and the platform copy
 * precede the POST. The second half is why the attempt lives in its own
 * object store, sets no key field of `courses-keys`, and goes to its own
 * platform slot: a member mid-attempt is byte-for-byte an unenrolled member.
 *
 * A RESTART RESUMES THE STORED ATTEMPT. `generateMasterKey` runs exactly once
 * per attempt, at the very start; while a row exists it is never called
 * again, so a lost 201 resumes to the same bundle and mints no second key.
 * From `prepared` the stored attempt is re-sent. From `ambiguous` it is NEVER
 * re-sent blind: one status read decides, matching on `keyId` + `signingKey`
 * (`enrolment-attempt.js` says why not `thisDevice`), and only a read that
 * returns a DIFFERENT bundle hands anyone to the approval screen. A 409 is
 * not a verdict; a 400, 404 or 503 authorises no discard.
 *
 * RECONCILING A SENT ATTEMPT IS RECOVERY, NOT ENROLMENT. The member may
 * already be enrolled, so it runs ahead of the `courses-e2ee` flag, the
 * eligibility probe, the persistence gate and the retry memos — the same rule
 * section 6.9 gives a member whose flag is switched off. What those gates
 * still decide is whether anything is SENT.
 *
 * ENROLMENT IS ONLY EVER ADDITIVE. A member who was not enrolled and fails to
 * enrol is left exactly as they were: notes readable, writes plaintext,
 * nothing deleted. No failure here, at any boundary, may make Start over the
 * member's only route forward.
 *
 * WHAT IT NEVER DOES: hold key material anywhere but `courses-keys` and the
 * attempt in flight, build a screen, or migrate the plaintext rows that
 * already exist (#1545).
 */
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { COURSES_E2EE_FLAG } from '@/constants/courses';
import { ATTEMPT_STATE } from '@/utils/courses-crypto/key-store';
import {
  API_ERROR_CODES,
  ENCRYPTION_ERROR_CODES,
  enrol,
  fetchEncryptionStatus,
  isEncryptionApiError,
} from '@/utils/courses-encryption-api';
import { coursesNotesAvailable } from '@/utils/courses-notes-availability';
import { resolveKeylessDevice } from './device-approval';
import { markBannerPending } from './encryption-banner';
import {
  VERDICT,
  adoptProvisionalCopy,
  discardAttempt,
  ensureCommittedCopy,
  finaliseAttempt,
  markSent,
  mintFreshDeviceId,
  postAttempt,
  prepareAttempt,
  proveProvisionalCopy,
  reconcileAttempt,
  verdictOf,
} from './enrolment-attempt';
import { ENROLMENT_OUTCOME, SKIP_REASON } from './enrolment-outcomes';
import { reconcileLocalKeys } from './reset';
import { describeThisDevice, newDeviceId } from './this-device';

/** The outcomes live in `enrolment-outcomes.js`; every caller still reads them from here. */
export { ENROLMENT_OUTCOME, SKIP_REASON };

/**
 * How long a 503 — or a 409 whose status read showed no bundle — holds off
 * the next send. Long enough that a member walking in and out of the Hub
 * does not POST every time, short enough that the promise row being filled
 * is noticed within one sitting.
 */
export const RETRY_AFTER_MS = 5 * 60 * 1000;

/** How long to wait for a hydration that is still in flight at the first open. */
export const HYDRATION_TIMEOUT_MS = 3000;

/** How many fresh device ids one attempt may mint on DEVICE_ID_IN_USE. */
export const MAX_DEVICE_ID_RETRIES = 1;

/**
 * The key-making half, loaded at the point of use.
 *
 * WHY LAZY, and it is not about bundle size. The Notes Hub and the reader's
 * highlight host both import this module to trigger an enrolment, and the
 * platform key adapter statically imports two NATIVE plugins
 * (`@capacitor/preferences`, `@aparajita/capacitor-secure-storage`). A static
 * import here would put both of them behind every page that can start an
 * enrolment — and behind every unit spec that touches one of those pages, none
 * of which has any business knowing the Keychain exists. Loading them inside
 * the one function that actually mints a key keeps the trigger cheap and the
 * dependency where it belongs. Memoised, so a second enrolment attempt in one
 * session does not re-resolve the modules. Exported for `stranded-state.js`,
 * whose diagnostic needs the adapter for one read.
 *
 * @type {Promise<object> | null}
 */
let keyToolsPromise = null;

export async function loadKeyTools() {
  if (!keyToolsPromise) {
    keyToolsPromise = Promise.all([
      import('@/utils/courses-crypto/master-key'),
      import('@/utils/courses-crypto/device-key'),
      import('@/utils/courses-crypto/bundle-key'),
      import('@/utils/courses-crypto/platform-key-adapter'),
      import('@/store/modules/coursesKeys'),
    ])
      .then(([masterKey, deviceKey, bundleKey, adapter, keysModule]) => ({
        generateMasterKey: masterKey.generateMasterKey,
        generateDeviceKeyPair: deviceKey.generateDeviceKeyPair,
        createWrappedSigningKey: bundleKey.createWrappedSigningKey,
        resolveAdapter: adapter.resolvePlatformAdapter,
        ENROLMENT_STATUS: keysModule.ENROLMENT_STATUS,
      }))
      .catch(error => {
        keyToolsPromise = null;
        throw error;
      });
  }
  return keyToolsPromise;
}

const DEFAULT_DEPS = Object.freeze({
  isEnabled: flag => useFeatureFlags().isEnabled(flag),
  notesAvailable: coursesNotesAvailable,
  describeThisDevice,
  newDeviceId,
  enrol,
  fetchEncryptionStatus,
  markBannerPending,
  reconcileLocalKeys,
  resolveKeylessDevice,
  now: () => Date.now(),
  loadKeyTools,
});

/**
 * Per-member, per-session memory of an answer that should not be asked again
 * immediately: a 503's retry window, a 404's "the flag is off", a 400's "the
 * body is wrong". None is persisted — a new launch asks once more, which is
 * how a flag flipped overnight, or a build that fixed the body, is picked up.
 *
 * @type {Map<number, { deferredUntil: number, serverFlagOff: boolean, rejected: boolean }>}
 */
const memos = new Map();

/** One enrolment per member at a time, whatever asks for it. */
const inFlight = new Map();

/** Members whose held key has had its once-per-session repair. */
const repaired = new Set();

/** Members whose provisional slot has been looked at this session, for section 5.3. */
const recovered = new Set();

/** Specs reset between cases; the app never calls this. */
export function resetEnrolmentStateForTests() {
  memos.clear();
  inFlight.clear();
  repaired.clear();
  recovered.clear();
}

/**
 * @param {number} memberId
 */
function memoFor(memberId) {
  if (!memos.has(memberId)) {
    memos.set(memberId, { deferredUntil: 0, serverFlagOff: false, rejected: false });
  }
  return memos.get(memberId);
}

const skipped = reason => ({ outcome: ENROLMENT_OUTCOME.SKIPPED, reason });

const SENT_STATES = [ATTEMPT_STATE.AMBIGUOUS, ATTEMPT_STATE.REJECTED];
const wasSent = state => SENT_STATES.includes(state);
/** `courses-keys`' word for "no row" (ATTEMPT_STATUS.NONE), spelled here to keep that module lazy. */
const ATTEMPT_STATE_NONE = 'none';

/**
 * A plain, loggable description of a failure. Deliberately not the error: the
 * caller is a page, and an `EncryptionApiError` carries an axios error as its
 * `cause` with the request config (and the bearer token) inside it.
 *
 * @param {unknown} error
 */
function describeError(error) {
  if (isEncryptionApiError(error)) {
    const { code, status, message, route } = error;
    return { code, status, message, route };
  }
  return { code: 'unknown', status: null, message: String(error?.message ?? error), route: null };
}

const errored = error => ({ outcome: ENROLMENT_OUTCOME.ERROR, error: describeError(error) });

/**
 * "Enrolled, but not here": the state the approval path reads. `courses-keys`
 * spells it PENDING — this device is waiting for another one to approve it, or
 * for a recovery code.
 *
 * @param {object} store
 */
function handOverToApproval(store, ENROLMENT_STATUS) {
  store.commit('courses-keys/setEnrolment', {
    status: ENROLMENT_STATUS.PENDING,
    hasRecoveryCode: store.getters['courses-devices/hasRecoveryCode'] === true,
  });
  return { outcome: ENROLMENT_OUTCOME.ENROLLED_ELSEWHERE };
}

/**
 * Before "this device already holds a key" becomes the terminal answer, once
 * per member per session: ask whether the key is live, and whether the
 * platform still carries it.
 *
 *   1. One status read and `reconcileLocalKeys` — the comparison the launch
 *      watcher makes, one step earlier, reusing the one function that knows
 *      the rotation exclusion. `refreshStatus` single-flights, so this joins
 *      the launch read already in the air, and it closes the ordering race
 *      where the Hub opens before that read has landed.
 *   2. The committed platform slot: written and read back if it does not hold
 *      this key. Repairs a finalisation whose copy write failed, and every
 *      member #1577 enrolled with the copy write swallowed.
 *
 * @param {{ store: object, deps: object, memberId: number }} args
 */
async function repairHeldKey({ store, deps, memberId }) {
  if (repaired.has(memberId)) return;
  repaired.add(memberId);
  let status = null;
  try {
    status = await store.dispatch('courses-devices/refreshStatus');
  } catch (error) {
    console.warn('[CoursesE2EE] The status read behind the held-key check did not land:', error);
  }
  if (status) {
    try {
      await deps.reconcileLocalKeys({ store, status });
    } catch (error) {
      console.warn('[CoursesE2EE] The held key was not reconciled:', error);
    }
  }
  const material = store.getters['courses-keys/keyMaterial'];
  if (!material) return;
  try {
    const tools = await deps.loadKeyTools();
    await ensureCommittedCopy({ tools, memberId, keyId: material.keyId, mk: material.mk });
  } catch (error) {
    console.warn('[CoursesE2EE] The platform copy of the held key was not asserted:', error);
  }
}

/**
 * Section 5.3, the recovery route, and it is recovery, so it runs ahead of
 * every send gate: a member with no key and no attempt row — signed out
 * mid-attempt, IndexedDB evicted between the commit and the promotion, or a
 * sibling phone that found the synced copy — whose provisional slot holds a
 * copy for exactly the bundle the server holds. Once per member per session;
 * a member with nothing in the slot costs one platform read and no request.
 *
 * @param {{ store: object, deps: object, memberId: number }} args
 * @returns {Promise<boolean>} whether a copy was adopted
 */
async function recoverProvisionalCopy({ store, deps, memberId }) {
  if (recovered.has(memberId)) return false;
  recovered.add(memberId);
  try {
    const tools = await deps.loadKeyTools();
    return await adoptProvisionalCopy({ store, tools, memberId });
  } catch (error) {
    console.warn('[CoursesE2EE] The provisional platform copy was not adopted:', error);
    return false;
  }
}

/**
 * Every precondition of SENDING a request (section 6.9): the flag, the build,
 * this device not already holding a key, the platform, the store, and the
 * memos. None of them gates reconciling an attempt already sent.
 *
 * @param {{ store: object, deps: object, memberId: number }} args
 * @returns {Promise<object | null>} a SKIPPED outcome, or null to go ahead
 */
async function sendGates({ store, deps, memberId }) {
  if (!deps.isEnabled(COURSES_E2EE_FLAG)) return skipped(SKIP_REASON.FLAG_OFF);
  if (!deps.notesAvailable()) return skipped(SKIP_REASON.NOTES_UNAVAILABLE);
  if (store.getters['courses-keys/hasMasterKey'] === true) {
    await repairHeldKey({ store, deps, memberId });
    if (store.getters['courses-keys/hasMasterKey'] === true) {
      return skipped(SKIP_REASON.ALREADY_HAS_KEY);
    }
  }
  if (store.getters['courses-keys/eligible'] !== true) return skipped(SKIP_REASON.NOT_ELIGIBLE);
  if (store.state['courses-keys']?.persistent !== true) return skipped(SKIP_REASON.NOT_PERSISTENT);
  if (memoFor(memberId).serverFlagOff) return skipped(SKIP_REASON.SERVER_FLAG_OFF);
  return readMemoGates({ deps, memberId });
}

/**
 * The answers this session already has that bound a READ as well as a send.
 * Consulted on the Hub-open path before the reconciling read of a sent
 * attempt: a 409, a 503 or a 404 within the window has already had its read,
 * and the loop is bounded to one GET and one POST per window rather than one
 * per Hub open; a rejected attempt has had its one read for the session.
 * The launch-time resume is not bounded by these; it runs once per session.
 * The server's flag being off is NOT among them — that bounds sending only,
 * because no response code authorises a skipped reconcile (section 6.7).
 *
 * @param {{ deps: object, memberId: number }} args
 * @returns {object | null}
 */
function readMemoGates({ deps, memberId }) {
  const memo = memoFor(memberId);
  if (memo.rejected) return skipped(SKIP_REASON.ATTEMPT_REJECTED);
  if (memo.deferredUntil > deps.now()) return skipped(SKIP_REASON.DEFERRED);
  return null;
}

/**
 * @param {object} ctx
 * @param {unknown} error
 */
function deferred(ctx, error) {
  memoFor(ctx.memberId).deferredUntil = ctx.deps.now() + RETRY_AFTER_MS;
  return { outcome: ENROLMENT_OUTCOME.DEFERRED, error: describeError(error) };
}

/**
 * The server holds this attempt's bundle: promote, publish, and only then the
 * banner. A promotion that fails leaves the attempt in place — the next open
 * reconciles it again — and never withholds the fact that the bundle exists.
 *
 * @param {object} ctx
 */
async function finalise(ctx) {
  const { store, deps, tools, memberId, attempt, verdict, resumed } = ctx;
  let result;
  try {
    result = await finaliseAttempt({
      store,
      deps,
      tools,
      memberId,
      attempt,
      status: verdict.status,
      registered: verdict.registered,
    });
  } catch (error) {
    // Only the promotion throws (finaliseAttempt says so); the row is still
    // there, and the next open reconciles it again.
    console.warn(
      '[CoursesE2EE] The bundle is registered but the key was not promoted here:',
      error,
    );
    return errored(error);
  }
  if (!verdict.registered) {
    // The server holds the bundle but not this install's device row (a
    // stripped header, or an id this client changed): register through the
    // signed self-approval path, which `register` answers idempotently.
    try {
      await deps.resolveKeylessDevice({ store });
    } catch (error) {
      console.warn('[CoursesE2EE] This install could not register itself after resuming:', error);
    }
  }
  deps.markBannerPending(memberId);
  // The body was the status shape, so the store is already correct; the read
  // that follows only refreshes `lastSeenAt`.
  await store.dispatch('courses-devices/refreshStatus').catch(() => {});
  return {
    outcome: ENROLMENT_OUTCOME.ENROLLED,
    keyId: attempt.keyId,
    deviceId: attempt.deviceId,
    resumed,
    published: result.published,
    platformCopyWritten: result.platformCopyWritten,
  };
}

/**
 * What a verdict means for the caller. Null for NOT_COMMITTED: that is the
 * one verdict whose remedy — a send — the caller has to gate.
 *
 * @param {object} ctx with `attempt`, `verdict` and `resumed`
 * @returns {Promise<object | null>}
 */
async function settle(ctx) {
  const { store, tools, memberId, attempt, verdict } = ctx;
  switch (verdict.kind) {
    case VERDICT.COMMITTED:
      return finalise(ctx);
    case VERDICT.SUPERSEDED:
    case VERDICT.REVOKED:
      // Proof: a different bundle, or a signed revoke — another holder of MK
      // demonstrably exists, which is the invariant's exit condition.
      try {
        await discardAttempt({ store, tools, memberId, attempt });
      } catch (error) {
        console.warn('[CoursesE2EE] The superseded attempt was not fully discarded:', error);
      }
      await store.dispatch('courses-devices/refreshStatus').catch(() => {});
      return handOverToApproval(store, tools.ENROLMENT_STATUS);
    case VERDICT.UNKNOWN:
      return errored(verdict.error ?? new Error('The status read carried no status'));
    default:
      return null;
  }
}

/**
 * A 409. Not a verdict: it covers "you have a bundle" — possibly the one this
 * attempt just committed — and "that device id is in use", and only the
 * explicit code or a read decides which.
 *
 * @param {object} ctx with the row as sent
 * @param {import('@/utils/courses-encryption-api').EncryptionApiError} error
 * @param {boolean} resent
 */
async function settleConflict(ctx, error, resent) {
  const { store, token, deps, tools, attempt } = ctx;
  if (error.apiCode === API_ERROR_CODES.DEVICE_ID_IN_USE) {
    // No bundle, and the id is taken: only a fresh id can succeed. Fires on
    // this explicit code alone, never on a count of uninterpretable 409s.
    if ((attempt.deviceIdRetries ?? 0) >= MAX_DEVICE_ID_RETRIES) return deferred(ctx, error);
    let fresh;
    try {
      fresh = await mintFreshDeviceId({ store, deps, tools, attempt });
    } catch (mintError) {
      return errored(mintError);
    }
    return sendAndSettle({ ...ctx, attempt: fresh }, resent);
  }
  const verdict = await reconcileAttempt({ token, deps, attempt });
  const settled = await settle({ ...ctx, verdict, resumed: false });
  if (settled) return settled;
  // 409 with no bundle to show for it: a read that raced the commit, or a
  // replica behind the write. One pair of requests per window, not a spin.
  return deferred(ctx, error);
}

/**
 * The POST failed. Which failures keep the attempt: all of them.
 *
 * @param {object} ctx with the row as sent
 * @param {unknown} error
 * @param {boolean} resent
 */
async function settleFailure(ctx, error, resent) {
  const { store, deps, memberId } = ctx;
  const memo = memoFor(memberId);
  if (isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.NOT_CONFIGURED)) {
    // The promise row is unfilled. Kept: a row filled ten minutes later must
    // not cost a fresh key.
    return deferred(ctx, error);
  }
  if (isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.NOT_FOUND)) {
    // The flag went off. Kept: the flag gates enrolling, not the key. Sending
    // stops for the session; the reconcile is only bounded to the window.
    memo.serverFlagOff = true;
    memo.deferredUntil = deps.now() + RETRY_AFTER_MS;
    return { outcome: ENROLMENT_OUTCOME.FLAG_OFF, error: describeError(error) };
  }
  if (isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.BAD_REQUEST)) {
    // A 400 on a body this client built is a client bug a retry reproduces.
    // Stop retrying, keep the bytes, say so loudly: a discard needs a status
    // read's proof, and the attempt is never re-sent on its own.
    console.warn('[CoursesE2EE] The API refused the enrolment body; the attempt is kept:', error);
    try {
      await store.dispatch('courses-keys/updateAttempt', {
        state: ATTEMPT_STATE.REJECTED,
        rejectedAt: deps.now(),
      });
    } catch (updateError) {
      console.warn('[CoursesE2EE] The refusal was not recorded on the attempt:', updateError);
    }
    memo.rejected = true;
    return errored(error);
  }
  if (isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT)) {
    return settleConflict(ctx, error, resent);
  }
  // Network, 5xx, a torn body: still ambiguous. The next open reconciles
  // before it sends anything.
  return errored(error);
}

/**
 * Send the stored attempt and read what came back. `ambiguous` is written
 * BEFORE the POST; if that write fails, nothing is sent.
 *
 * @param {object} ctx with `attempt`
 * @param {boolean} [resent] whether this is already the one permitted re-send
 */
async function sendAndSettle(ctx, resent = false) {
  const { store, token, deps } = ctx;
  let attempt;
  try {
    attempt = await markSent({ store, deps, attempt: ctx.attempt });
  } catch (error) {
    return errored(error);
  }
  let status;
  try {
    status = await postAttempt({ token, deps, attempt });
  } catch (error) {
    return settleFailure({ ...ctx, attempt }, error, resent);
  }
  // The 201 body IS the status body for the same `X-Device-Id` (contract
  // section 4), so it is read the same way a reconciling GET is.
  const verdict = verdictOf(status, attempt);
  if (verdict.kind === VERDICT.NOT_COMMITTED) {
    // api#1117: a replay racing a lazily-completing hold can answer 201 with
    // no bundle. Once more, then wait out the window.
    if (!resent) return sendAndSettle({ ...ctx, attempt }, true);
    return deferred(ctx, new Error('The API answered 201 with no bundle'));
  }
  return settle({ ...ctx, attempt, verdict, resumed: false });
}

/**
 * A `prepared` attempt: prove the provisional copy, then send.
 *
 * @param {object} ctx with `attempt`
 */
async function continuePrepared(ctx) {
  const { tools, memberId, attempt } = ctx;
  try {
    if (!(await proveProvisionalCopy({ tools, memberId, attempt }))) {
      // Eligible, and the write did not read back. Not "ineligible", which
      // the nudge already handles: a surprise, and never reported as success.
      return errored(new Error('The platform copy of the key did not read back'));
    }
  } catch (error) {
    return errored(error);
  }
  return sendAndSettle(ctx);
}

/**
 * An attempt already sent: reconcile before anything else, ungated. Only
 * when the read shows no bundle does sending come into it, and that is an
 * enrolment, so it passes the gates — and a `rejected` attempt is never sent
 * again on its own (section 6.7): the read may still promote or discard it,
 * and that is all.
 *
 * @param {object} ctx with `attempt`
 */
async function resumeSent(ctx) {
  const { store, token, deps, memberId, attempt } = ctx;
  const verdict = await reconcileAttempt({ token, deps, attempt });
  const settled = await settle({ ...ctx, verdict, resumed: true });
  if (settled) return settled;
  if (attempt.state === ATTEMPT_STATE.REJECTED) {
    console.warn('[CoursesE2EE] A rejected enrolment attempt is kept and not re-sent:', {
      memberId,
      keyId: attempt.keyId,
    });
    memoFor(memberId).rejected = true;
    return skipped(SKIP_REASON.ATTEMPT_REJECTED);
  }
  const blocked = await sendGates({ store, deps, memberId });
  if (blocked) return blocked;
  return sendAndSettle(ctx);
}

/**
 * No attempt: the fresh path. The API's own answer first — a member who
 * enrolled on another phone must land on the approval path, not register a
 * second bundle and collect a 409 for it.
 *
 * @param {object} ctx
 */
async function startAttempt(ctx) {
  const { store, deps, tools, memberId } = ctx;
  try {
    await store.dispatch('courses-devices/refreshStatus');
  } catch (error) {
    return errored(error);
  }
  if (store.getters['courses-devices/enrolled'] === true) {
    return handOverToApproval(store, tools.ENROLMENT_STATUS);
  }
  let attempt;
  try {
    attempt = await prepareAttempt({ store, deps, tools, memberId });
  } catch (error) {
    return errored(error);
  }
  return continuePrepared({ ...ctx, attempt });
}

/**
 * The whole of one enrolment or resume, from the stored attempt outward.
 * Only ever reached with the hydration settled and the member known.
 *
 * @param {{ store: object, token: string | null, deps: object, memberId: number }} ctx
 */
async function runEnrolment(ctx) {
  // The crypto and the platform adapter, resolved before anything else so a
  // module that will not load is a plain error rather than a half-enrolment.
  let tools;
  try {
    tools = await ctx.deps.loadKeyTools();
  } catch (error) {
    return errored(error);
  }
  // THE FIRST THING READ. On a resume after a lost 201 the API says
  // `enrolled: true`, and that is an input to the reconcile, not a verdict.
  let attempt;
  try {
    attempt = await ctx.store.dispatch('courses-keys/loadAttempt');
  } catch (error) {
    return errored(error);
  }
  const run = { ...ctx, tools, attempt };
  if (!attempt) return startAttempt(run);
  if (attempt.state === ATTEMPT_STATE.PREPARED) return continuePrepared(run);
  return resumeSent(run);
}

/**
 * The shared entry: settle the hydration, know the member, gate a SEND but
 * never a reconcile, and single-flight per member.
 *
 * @param {{ store: object, auth?: object, deps?: object, recoveryOnly: boolean }} args
 */
async function enter({ store, auth, deps: overrides, recoveryOnly }) {
  if (!store) throw new TypeError('enrolIfEligible needs the root Vuex store');
  const deps = overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS;

  // A deep link into the Hub can beat the hydration App.vue starts, and a
  // member whose keys have not been read yet is not a member without keys.
  if (store.getters['courses-keys/isHydrated'] !== true) {
    try {
      await store.dispatch('courses-keys/ready', { timeoutMs: HYDRATION_TIMEOUT_MS });
    } catch (error) {
      return skipped(SKIP_REASON.NOT_HYDRATED);
    }
  }

  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) return skipped(SKIP_REASON.NO_MEMBER);

  const attemptState = store.getters['courses-keys/attemptState'];
  const sent = wasSent(attemptState);
  if (recoveryOnly && !sent) return skipped(SKIP_REASON.NO_ATTEMPT);

  // Recovery first, ungated: a sent attempt is reconciled whatever the gates
  // say (the Hub-open path only bounds the read to the retry window), and a
  // member with no key and no row may be holding a provisional copy of the
  // bundle the server already has.
  if (sent) {
    if (!recoveryOnly) {
      const blocked = readMemoGates({ deps, memberId });
      if (blocked) return blocked;
    }
  } else {
    const keyless =
      attemptState === ATTEMPT_STATE_NONE && store.getters['courses-keys/hasMasterKey'] !== true;
    if (keyless && (await recoverProvisionalCopy({ store, deps, memberId }))) {
      const { ENROLMENT_STATUS } = await deps.loadKeyTools();
      return handOverToApproval(store, ENROLMENT_STATUS);
    }
    const blocked = await sendGates({ store, deps, memberId });
    if (blocked) return blocked;
  }

  const running = inFlight.get(memberId);
  if (running) return running;

  const token = auth?.token ?? store.state.user?.token ?? null;
  const run = (async () => {
    try {
      return await runEnrolment({ store, token, deps, memberId });
    } finally {
      if (inFlight.get(memberId) === run) inFlight.delete(memberId);
    }
  })();
  inFlight.set(memberId, run);
  return run;
}

/**
 * Enrol this member and this device, if every precondition holds — or
 * resume the attempt this device already has. Safe to call on every Notes
 * Hub open and before every highlight save: an enrolled member costs one
 * getter read, plus one status read per session.
 *
 * Never rejects for an expected failure — the caller is a page open, and a
 * screen must not break because a bundle could not be registered. Every answer
 * comes back as an outcome.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {{ token?: string }} [args.auth] overrides the store's bearer token
 * @param {object} [args.deps] injected for tests; the app passes none
 * @returns {Promise<{ outcome: string, reason?: string, error?: object, keyId?: string,
 *   deviceId?: string, resumed?: boolean, platformCopyWritten?: boolean }>}
 */
export function enrolIfEligible({ store, auth, deps } = {}) {
  return enter({ store, auth, deps, recoveryOnly: false });
}

/**
 * The enrolment this member has running right now, or null.
 *
 * For the SEALING HOOK, and for one state only: between the server accepting the
 * enrol POST and this device promoting its key, a write finds the member
 * enrolled and this device holding nothing — which is indistinguishable from a
 * second, unapproved device, and is refused as `NO_KEY`. That refusal drops the
 * slot and rolls a blank answer back, so a member who enrols BY TYPING would
 * lose the very words that enrolled them.
 *
 * Exposing the promise lets the hook wait for the state to end rather than
 * guess. It settles to an outcome for every expected failure, so waiting on it
 * is bounded by the enrolment itself and not by a timeout invented here.
 *
 * @param {number} memberId the id `enrolIfEligible` single-flights on —
 *   `courses-keys/memberId`
 * @returns {Promise<object> | null}
 */
export function enrolmentInFlight(memberId) {
  return inFlight.get(memberId) ?? null;
}

/**
 * Reconcile an attempt this device has already sent, whatever the gates say:
 * the launch-time trigger (`enrolment-resume.js`). A member with nothing sent
 * is left alone. Sending, if the read says nothing committed, still passes
 * every gate.
 *
 * @param {object} args as `enrolIfEligible`
 */
export function resumeEnrolment({ store, auth, deps } = {}) {
  return enter({ store, auth, deps, recoveryOnly: true });
}

const recoveredVia = via => ({ recovered: true, via });
const notRecovered = reason => ({ recovered: false, reason });

/**
 * #1652, Try again on the stranded screen: the two recovery routes above on
 * demand, with this member's once-per-session latches set aside. Nothing is
 * sent (the member is enrolled on the server; a fresh bundle would only be
 * refused), and an attempt row the reconcile leaves standing keeps the slot,
 * since adopting under it would be the blind promotion decision 40 forbids.
 *
 * @param {{ store: object, auth?: { token?: string }, deps?: object }} args
 * @returns {Promise<{ recovered: boolean, via?: string, reason?: string }>}
 */
export async function retryRecovery({ store, auth, deps: overrides } = {}) {
  if (!store) throw new TypeError('retryRecovery needs the root Vuex store');
  const deps = overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS;
  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) return notRecovered(SKIP_REASON.NO_MEMBER);
  recovered.delete(memberId);
  repaired.delete(memberId);
  await enter({ store, auth, deps, recoveryOnly: true }).catch(error => {
    console.warn('[CoursesE2EE] Try again could not reconcile the sent attempt:', error);
  });
  if (store.getters['courses-keys/hasMasterKey'] === true) return recoveredVia('attempt');
  if (store.getters['courses-keys/attemptState'] !== ATTEMPT_STATE_NONE) {
    return notRecovered('attempt-unresolved');
  }
  if (!(await recoverProvisionalCopy({ store, deps, memberId }))) {
    return notRecovered('nothing-to-adopt');
  }
  handOverToApproval(store, (await deps.loadKeyTools()).ENROLMENT_STATUS);
  return recoveredVia('provisional-copy');
}
